import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface User {
    id: string;
    username?: string;
    ingameName?: string;
    avatarUrl?: string;
    teamId?: string | null;
    teamRole?: string;
    csRole?: string;
    isBanned?: boolean;
    isAdmin?: boolean;
    isSemiAdmin?: boolean;
    isManager?: boolean;
    permissionOverride?: string;
    faceitUrl?: string;
    faceitName?: string;
    isPremium?: boolean;
    premiumExpiresAt?: string;
    premiumRecurring?: boolean;
    stripeSubId?: string | null;
    faceitElo?: number;
    faceitLevel?: number;
    createdAt?: string;
    team?: {
        id: string;
        ownerId: string;
        name?: string;
        faceitUrl?: string;
        faceitName?: string;
        isPremium?: boolean;
        premiumTier?: string;
        premiumExpiresAt?: string;
        premiumExpiredAt?: string;
        premiumRecurring?: boolean;
        freeServerStarts?: number;
        freeAnalysisStarts?: number;
    };
    teamMemberships?: Array<{
        id: string;
        role: string;
        team: {
            id: string;
            name: string;
            logoUrl?: string;
            ownerId?: string;
            isPremium?: boolean;
            premiumTier?: string;
            premiumExpiresAt?: string;
            premiumRecurring?: boolean;
        };
    }>;
    notificationPrefs?: any;
}

interface AuthContextType {
    personalUserId?: string;
    user: User | null;
    token: string | null;
    login: (token: string, user: User) => void;
    logout: () => void;
    isAuthenticated: boolean;
    isLoading: boolean;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setAuthToken] = useState<string | null>(localStorage.getItem('token'));
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                localStorage.setItem('token', token);
                try {
                    await axios.post(`${API}/auth/media-session`);
                    const res = await axios.get(`${API}/users/me`);
                    setUser(res.data);
                } catch (err) {
                    console.error('Failed to fetch user', err);
                    setAuthToken(null);
                    setUser(null);
                    localStorage.removeItem('token');
                }
            } else {
                delete axios.defaults.headers.common['Authorization'];
                localStorage.removeItem('token');
                setUser(null);
            }
            setIsLoading(false);
        };
        initAuth();
    }, [token]);

    useEffect(() => {
        if (!token || !user) return;
        const heartbeat = () => {
            void axios.post(`${API}/auth/activity`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            }).catch(() => { /* A lost heartbeat expires naturally; do not sign users out. */ });
        };
        heartbeat();
        const timer = window.setInterval(heartbeat, 30_000);
        window.addEventListener('focus', heartbeat);
        return () => { window.clearInterval(timer); window.removeEventListener('focus', heartbeat); };
    }, [token, user?.id]);

    const login = useCallback((newToken: string, newUser: User) => {
        setAuthToken(newToken);
        setUser(newUser);
    }, []);

    const refreshUser = useCallback(async () => {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) return;
        try {
            const res = await axios.get(`${API}/users/me`);
            setUser(res.data);
        } catch (err) {
            console.error('Failed to refresh user', err);
        }
    }, []);

    const logout = useCallback(() => {
        setAuthToken(null);
        setUser(null);
        localStorage.removeItem('token');
    }, []);

    const value = useMemo(() => ({
        user,
        token,
        login,
        logout,
        refreshUser,
        isAuthenticated: !!token,
        isLoading,
    }), [user, token, login, logout, refreshUser, isLoading]);

    return (
        <AuthContext.Provider value={value}>
            <div data-build-id="cache-bypass-101">
                {children}
            </div>
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/** Route-local user view; the real admin login and stored token are untouched. */
export function PersonalUserProvider({ children, user, refreshUser }: { children: ReactNode; user: User; refreshUser: () => Promise<void> }) {
    const auth = useAuth();
    return <AuthContext.Provider value={{ ...auth, user, refreshUser, personalUserId: user.id }}>{children}</AuthContext.Provider>;
}
